/**
 * API Client Utility
 *
 * Wraps axios to automatically attach the OIDC access token (received after
 * Okta PKCE login) to all outgoing API requests as an Authorization header.
 *
 * Token flow:
 *   1. User logs in via Okta PKCE (authorization_code grant)
 *   2. The OIDC access_token is stored in localStorage as 'okta_api_token'
 *   3. This interceptor attaches it as Authorization: Bearer <token> to all API calls
 *   4. The Lambda authorizer validates the token at API Gateway
 */

import axios from 'axios';

// Create an axios instance for API calls
const apiClient = axios.create();

// Request interceptor — attach the OIDC access token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('okta_api_token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — handle 401 Unauthorized (token expired / invalid)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn('API returned 401/403 — token may be expired or invalid');

      try {
        const userString = localStorage.getItem('user');
        if (userString) {
          const userData = JSON.parse(userString);
          const expiresAt = new Date(userData.expires_at).getTime();
          if (Date.now() > expiresAt) {
            // Token is expired — clear session and redirect to login
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('okta_api_token');
            window.location.href = '/login';
            return Promise.reject(error);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Get the current OIDC access token.
 * Useful for non-axios calls (e.g., fetch, FormData uploads).
 * @returns {string|null} The Bearer token or null
 */
export function getApiToken() {
  return localStorage.getItem('okta_api_token') || null;
}

/**
 * Build Authorization headers for API calls.
 * @returns {Object} Headers object with Authorization if token exists
 */
export function getAuthHeaders() {
  const token = getApiToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default apiClient;
